import { useState } from 'react';
import { PageHeader } from '../components/Layout.jsx';
import {
  Alert, Card, Empty, ErrorState, Input, Loading, NumberInput, Select, Stat, Textarea,
} from '../components/ui.jsx';
import { Modal, ConfirmModal } from '../components/Modal.jsx';
import { TankGauge } from './Dashboard.jsx';
import { IconPlus } from '../components/Icons.jsx';
import { api } from '../lib/api.js';
import { useApi } from '../lib/hooks.js';
import { useAuth } from '../lib/auth.jsx';
import { useToast } from '../lib/toast.jsx';
import { t } from '../lib/i18n.js';
import { formatDate, liters, num } from '../lib/format.js';

export default function Tanks() {
  const toast = useToast();
  const { isAdmin } = useAuth();
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [busy, setBusy] = useState(false);
  const { data, error, loading, reload } = useApi('/api/tanks', { include_inactive: true });

  const remove = async () => {
    setBusy(true);
    try {
      await api.del(`/api/tanks/${deleting.id}`);
      toast.success(t.tank.deleted);
      setDeleting(null);
      reload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  const totals = (data || []).reduce(
    (acc, tank) => ({
      capacity: acc.capacity + Number(tank.capacity_liters),
      current: acc.current + Number(tank.current_liters),
    }),
    { capacity: 0, current: 0 },
  );

  return (
    <>
      <PageHeader
        title={t.tank.title}
        subtitle={t.tank.subtitle}
        actions={
          <button type="button" className="btn btn-sm btn-primary no-print" onClick={() => setEditing({})}>
            <IconPlus /> {t.common.add}
          </button>
        }
      />

      <div className="page">
        <div className="stat-grid">
          <Stat label={t.tank.inStorage} value={liters(totals.current)} hint={t.tank.ofCapacity(liters(totals.capacity))} />
          <Stat
            label={t.tank.spaceFree}
            value={liters(Math.max(0, totals.capacity - totals.current))}
            hint={t.tank.tankCount(num((data || []).length))}
          />
        </div>

        <ErrorState error={error} onRetry={reload} />
        {loading && !data && <Loading />}

        {data && data.length === 0 && (
          <Card>
            <Empty
              title={t.tank.emptyTitle}
              message={t.tank.emptyHint}
              action={
                <button type="button" className="btn btn-primary" onClick={() => setEditing({})}>
                  <IconPlus /> {t.tank.addOne}
                </button>
              }
            />
          </Card>
        )}

        {data &&
          data.map((tank) => (
            <Card key={tank.id}>
              <TankGauge tank={tank} />
              {tank.notes && <p className="small muted mt-0">{tank.notes}</p>}
              <div className="btn-row no-print">
                <button type="button" className="btn btn-sm" onClick={() => setViewing(tank)}>
                  {t.tank.movements}
                </button>
                <button type="button" className="btn btn-sm btn-ghost" onClick={() => setEditing(tank)}>
                  {t.common.edit}
                </button>
                {isAdmin && (
                  <button type="button" className="btn btn-sm btn-danger" onClick={() => setDeleting(tank)}>
                    {t.common.delete}
                  </button>
                )}
                {!tank.active && <span className="badge muted">{t.common.inactive}</span>}
              </div>
            </Card>
          ))}
      </div>

      {editing && (
        <TankForm
          tank={editing}
          onClose={() => setEditing(null)}
          onSaved={(saved) => {
            setEditing(null);
            toast.success(t.tank.saved(saved.name));
            reload();
          }}
        />
      )}

      {viewing && <TankMovements tank={viewing} onClose={() => setViewing(null)} />}

      {deleting && (
        <ConfirmModal
          title={t.tank.deleteTitle}
          message={t.tank.deleteMessage(deleting.name)}
          onConfirm={remove}
          onClose={() => setDeleting(null)}
          busy={busy}
        />
      )}
    </>
  );
}

function TankForm({ tank, onClose, onSaved }) {
  const isEdit = Boolean(tank.id);
  const [form, setForm] = useState({
    name: tank.name || '',
    capacity_liters: tank.capacity_liters || '',
    opening_liters: tank.opening_liters || '0',
    notes: tank.notes || '',
    active: tank.active ?? true,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const saved = isEdit ? await api.patch(`/api/tanks/${tank.id}`, form) : await api.post('/api/tanks', form);
      onSaved(saved);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      title={isEdit ? t.tank.editTitle : t.tank.newTitle}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={busy}>
            {t.common.cancel}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={save}
            disabled={busy || !form.name.trim() || !(Number(form.capacity_liters) > 0)}
          >
            {busy ? <span className="spinner" /> : t.common.save}
          </button>
        </>
      }
    >
      {error && <Alert tone="error">{error}</Alert>}
      <Input label={t.tank.tankName} required value={form.name} onChange={(e) => set({ name: e.target.value })} />
      <NumberInput
        label={t.tank.capacity}
        required
        value={form.capacity_liters}
        onChange={(capacity_liters) => set({ capacity_liters })}
      />
      <NumberInput
        label={t.tank.openingVolume}
        value={form.opening_liters}
        onChange={(opening_liters) => set({ opening_liters })}
        hint={t.tank.openingHint}
      />
      <Textarea label={t.common.notes} value={form.notes} onChange={(e) => set({ notes: e.target.value })} rows={2} />
      {isEdit && (
        <Select
          label={t.common.status}
          value={form.active ? 'true' : 'false'}
          onChange={(e) => set({ active: e.target.value === 'true' })}
          options={[
            { value: 'true', label: t.common.active },
            { value: 'false', label: t.common.inactive },
          ]}
        />
      )}
    </Modal>
  );
}

function TankMovements({ tank, onClose }) {
  const { data, loading, error } = useApi(`/api/tanks/${tank.id}/movements`);
  return (
    <Modal title={t.tank.movementsOf(tank.name)} onClose={onClose} wide>
      <ErrorState error={error} />
      {loading && !data && <Loading />}
      {data && data.length === 0 && <Empty title={t.tank.noMovements} />}
      {data && data.length > 0 && (
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>{t.common.date}</th>
                <th>{t.tank.direction}</th>
                <th>{t.shared.ref}</th>
                <th>{t.shared.party}</th>
                <th className="num">{t.report.litersCol}</th>
              </tr>
            </thead>
            <tbody>
              {data.map((move) => (
                <tr key={`${move.direction}-${move.id}`}>
                  <td>{formatDate(move.moved_on)}</td>
                  <td>
                    <span className={`badge ${move.direction === 'in' ? 'buy' : 'sell'}`}>
                      {move.direction === 'in' ? t.tank.in : t.tank.out}
                    </span>
                  </td>
                  <td>{move.ref_no}</td>
                  <td className="wrap">{move.party_name}</td>
                  <td className="num">
                    {move.direction === 'in' ? '+' : '−'}
                    {liters(move.liters)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
}
